import React, { useEffect, useMemo, useCallback } from 'react';
import { TbLayoutDashboard, TbChecklist, TbSearch, TbShoppingBag, TbShoppingBagSearch } from "react-icons/tb";
import { useLoginContext } from '../../Contexts/LoginContext';
import { Link, useLocation } from 'react-router-dom';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useStoreContext } from '../../Contexts/StoreContext';
import { formatCartId, formatCurrency } from '../../Utils/Formatters';
import { HiBars3 } from 'react-icons/hi2';
import Search from './Search';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { useCartContext } from '../../Contexts/CartContext';
import ButtonOpenStoreCounter from './ButtonOpenStoreCounter';
import ButtonPrimary from '../Form/ButtonPrimary';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import SelectedProduct from './Product/SelectedProduct';
import { isIOS } from '../../Utils/Helpers';
import NotifyCartView from './NotifyCartView';
import { useCheckoutContext } from '../../Contexts/CheckoutContext';
import { useTranslation } from 'react-i18next';
import { useOrderHook } from '../../Hooks/OrderHook';
import BadgeIndicator from './Product/BadgeIndicator';

const defaultClassNameButtonCart = `
    relative
    shadow-reverse-md
    shadow-black/30
    rounded-none
    rounded-t-xl
    px-1
    mx-0.5
    absolute
    z-50
    -top-2
    min-w-24 max-w-24
    min-h-[80px] max-h-[80px]
    leading-none
`;

const defaultClassName = `
    flex flex-col items-center justify-center
    w-full h-full
    gap-0.5
    z-30
    transition ease-in-out duration-400
    disabled:pointer-events-none
    disabled:text-gray-300
    text-xs
`;

const linkActived = 'pointer-events-none text-custom-primary';
const linkDisabled = 'pointer-events-none opacity-50';

const MenuMobile = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { getLinkToHome } = useStoreHook();
    const { getOrdersToday, handleShowCart } = useOrderHook();
    const { currentStore, cashier } = useStoreContext();
    const isHome = location.pathname === getLinkToHome();
    const isProductPage = location.pathname.startsWith(`${getLinkToHome() === '/' ? '' : getLinkToHome()}/produto/`);
    const { showSearchBar, setShowSearchBar, setShowMainMenu, setShowTodayOrders, isCategoriesSwiperViewed, isCartInfoViewed, setIsCartInfoViewed, isMobile } = useLayoutContext();
    const { isStoreCounter, orderType, isMenu } = useOrderTypeContext();
    const { isAuthenticated, isAuthenticatedToQuickPurchase, user } = useLoginContext();
    const { order, awaitingOrPendingCount, setAwaitingOrPendingCount } = useCheckoutContext();
    const { cartConference, cartDisplay, cartFinished, getTotalQuantityProductsInConference, setShowCart, setShowCartConference, currentCart, loadingUpdateQuantity, loadingCurrentCart } = useCartContext();

    const enableButtonOpenStoreCounter = Boolean((cashier || cartFinished || cartDisplay) && currentStore?.customer_self_attendance);
    const showButtonOpenStoreCounter = (!isAuthenticated && !isAuthenticatedToQuickPurchase) || !cartDisplay;
    const loadingCurrentCartOrpdateQuantity = loadingCurrentCart || loadingUpdateQuantity;

    const labelOpenStoreCounter = useMemo(() => (
        <div className='flex flex-col items-center justify-center leading-5 gap-0.5 -mt-2 pb-0.5'>
            <span className='font-black text-[1.6rem] leading-none'>{t('menu.mobile.store_counter.button_open_label')}</span>
            <span className='opacity-70 text-xs leading-none'>{t('store_counter.name')}</span>
        </div>
    ), [t]);

    const handleClickCart = useCallback(() => {
        setShowSearchBar(false);
        setShowCart(true);
    }, [setShowSearchBar, setShowCart]);

    const pluralConference = Boolean(getTotalQuantityProductsInConference(cartConference) > 1);

    useEffect(() => {
        if (isStoreCounter || !user?.id || (!isAuthenticated && !isAuthenticatedToQuickPurchase) || orderType == null) {
            setAwaitingOrPendingCount(0);
            return;
        }
        if (!isMenu) {
            getOrdersToday();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, isAuthenticated, isAuthenticatedToQuickPurchase, orderType]);

    const handleClickOrdersButton = useCallback(() => {
        if (isStoreCounter) {
            handleShowCart(order)
        } else {
            setShowSearchBar(false);
            setShowTodayOrders(true);
        }
    }, [isStoreCounter, handleShowCart, order, setShowSearchBar, setShowTodayOrders]);

    const handleSearch = useCallback(() => {}, []);
    const handleConfirmNotifyCart = useCallback(() => { setShowSearchBar(false); setIsCartInfoViewed(true) }, [setShowSearchBar, setIsCartInfoViewed]);
    const handleCloseSearch = useCallback(() => setShowSearchBar(false), [setShowSearchBar]);
    const handleToggleSearch = useCallback(() => setShowSearchBar(!showSearchBar), [showSearchBar, setShowSearchBar]);
    const handleClickShowCart = useCallback(() => handleShowCart(order), [handleShowCart, order]);
    const handleClickCartConference = useCallback(() => { setShowCartConference(true); setShowSearchBar(false) }, [setShowCartConference, setShowSearchBar]);
    const handleClickMainMenu = useCallback(() => { setShowMainMenu(true); setShowSearchBar(false); }, [setShowMainMenu, setShowSearchBar]);

    return (
        <>

            {showSearchBar &&
                <div className={`bottom-14 fixed md:hidden z-25 w-full p-3 bg-gradient-to-b from-white/25 to-white/35 backdrop-blur-sm rounded-t-xl shadow-reverse-md
                    ${!isIOS() ? 'transition-opacity duration-150' : ''}
                    ${showSearchBar ? 'bottom-14 opacity-100' : 'bottom-0 opacity-0'}
                    `}
                    style={{ willChange: 'transform' }}>
                    <Search onSearching={handleSearch} alwaysDisplayCloseButton />
                </div>
            }

            {/* NOTIFICAÇÃO DE VISUALIZAÇÃO DA COMANDA - AUTO ATENDIMENTO */}
            <NotifyCartView
                show={isMobile && isCategoriesSwiperViewed && isCartInfoViewed === false && isStoreCounter}
                onClickConfirm={handleConfirmNotifyCart}
            />

            <div className='fixed md:hidden h-14 left-0 right-0 bottom-0 z-30 flex text-xs font-semibold bg-white shadow-reverse-md'>

                <Link to={getLinkToHome()} onClick={handleCloseSearch} className={`
                    ${defaultClassName}
                    ${(isHome || isProductPage) && !showSearchBar && linkActived}
                    ${!currentStore && linkDisabled}
                    `}>
                    {isHome ? <TbLayoutDashboard size={23} className='zoomInIconNoDelay' /> : <TbLayoutDashboard size={23} /> }
                    {t('menu.mobile.home')}
                </Link>

                <button
                    onClick={handleToggleSearch}
                    className={`
                        ${showSearchBar && linkActived}
                        ${defaultClassName}`
                    }>
                    {showSearchBar ? <TbSearch size={23} className='zoomInIconNoDelay' /> : <TbSearch size={23} /> }
                    {t('menu.mobile.search')}
                </button>

                {/* BOTÃO PRINCIPAL - AUTO ATENDIMENTO */}
                {(isStoreCounter && enableButtonOpenStoreCounter) &&
                    <div className={`relative`}>
                        {showButtonOpenStoreCounter
                            ?
                                <ButtonOpenStoreCounter
                                    label={labelOpenStoreCounter}
                                    forceLabel
                                    showArrows={false}
                                    className={`${defaultClassNameButtonCart}`}
                                    inMenuFooter
                                />
                            :
                                cartConference.length === 0
                                    ?
                                        <ButtonPrimary onClick={handleClickShowCart} disabled={!cartDisplay} className={`relative ${defaultClassNameButtonCart}`}>
                                            <div className='flex flex-col gap-2 justify-evenly text-center w-full absolute top-0 mt-1.5'>
                                                <div className="w-1/4 bg-white h-0.5 m-auto rounded-full" />
                                                <div className='flex flex-col gap-0.5 mt-2 text-center text-2xl leading-none absolute inset-0'>
                                                    <div className={`text-[1.7rem] font-black ${isCategoriesSwiperViewed && isCartInfoViewed === false ? 'animate-pulse' : '' }`}>{ formatCartId(cartDisplay?.display_id || cartDisplay?.id) }</div>
                                                    <div className="text-xs opacity-80 font-regular leading-none">{t('store_counter.name')}</div>
                                                </div>
                                            </div>
                                        </ButtonPrimary>
                                    :
                                        <ButtonPrimary onClick={handleClickCartConference} className={`${defaultClassNameButtonCart} flex-col items-center pt-0.5 px-[2px] justify-center gap-1`}>
                                            <div className='flex flex-col gap-2 justify-evenly text-center w-full absolute top-0 mt-1.5'>
                                                <div className="w-1/4 bg-white h-0.5 m-auto rounded-full" />
                                                <div className="flex flex-col gap-1 mt-1.5">
                                                    <span className='opacity-70 text-xs leading-none font-regular -mt-1'>{pluralConference ? t('store_counter.button_check_and_send_label_plural_menu_mobile') : t('store_counter.button_check_and_send_label_menu_mobile')}</span>
                                                    <span className='font-black text-xl leading-none animate-pulse'>{pluralConference ? `${getTotalQuantityProductsInConference(cartConference)} ${t('layout.item_label_plural')}` : `${getTotalQuantityProductsInConference(cartConference)} ${t('layout.item_label_singular')}`}</span>
                                                </div>
                                            </div>
                                        </ButtonPrimary>
                        }

                    </div>
                }

                {/* BOTÃO PRINCIPAL - OUTROS SERVICOS */}
                {(!isStoreCounter && currentCart) &&
                    <ButtonPrimary onClick={handleClickCart} className={`${defaultClassNameButtonCart} relative`}>

                        {(loadingCurrentCartOrpdateQuantity || currentCart.items.length > 0) &&
                            <SelectedProduct
                                loading={loadingCurrentCartOrpdateQuantity}
                                animated={!loadingCurrentCartOrpdateQuantity}
                                colorAnimation='white'
                                quantity={currentCart ? getTotalQuantityProductsInConference(currentCart.items) : 0}
                                className="-top-1 -right-1.5"
                            />
                        }

                        <div className='absolute inset-0 flex flex-col gap-0.5 justify-center items-center text-center w-full -mt-4'>
                            {!loadingCurrentCartOrpdateQuantity && currentCart.items.length > 0 && <div className="text-[8px] opacity-70 leading-none uppercase">{t('cart.value_details.total')} R$</div>}

                            <div className={`flex items-center justify-center gap-0.5 text-center w-full ${(loadingCurrentCartOrpdateQuantity || currentCart.items.length === 0) && 'flex-col'}`}>
                                {(loadingCurrentCartOrpdateQuantity || currentCart.items.length === 0) && <TbShoppingBag size={29} className='zoomInIconNoDelay' />}
                                {(!loadingCurrentCartOrpdateQuantity && currentCart.items.length > 0) && <span className='text-xl leading-none font-black'>{formatCurrency(Number(currentCart.total_with_discount) + Number(currentCart.extra_tax), true, false)}</span>}
                            </div>

                            <div className="text-xs leading-none mt-0.5 h-3">
                                {loadingCurrentCartOrpdateQuantity
                                    ? <div className="bg-white/50 rounded-full w-14 h-1.5 mt-1 animate-pulse leading-none"></div>
                                    : t('menu.mobile.cart')
                                }
                            </div>
                        </div>
                    </ButtonPrimary>
                }

                {!isMenu &&
                    <button className={defaultClassName}
                    onClick={handleClickOrdersButton}
                    disabled={(isStoreCounter && !cartDisplay) || (!isAuthenticated && !isAuthenticatedToQuickPurchase)}
                    >
                        <div className='relative'>
                            {isStoreCounter
                                ?   <TbShoppingBagSearch size={23} className='zoomInIconNoDelay' />
                                :   <TbChecklist size={23} className='zoomInIconNoDelay' />
                            }
                            { awaitingOrPendingCount > 0 && <BadgeIndicator quantity={awaitingOrPendingCount} className='absolute -top-3.5 -right-3.5 bg-shopfood-notify animate-bounce' />}
                        </div>
                        {t('menu.mobile.orders')}
                    </button>
                }

                <button onClick={handleClickMainMenu} className={defaultClassName}>
                    <HiBars3 size={23} />
                    {t('menu.mobile.others')}
                </button>

            </div>

        </>
    )
}

export default MenuMobile;